import { useEffect, useState } from 'react'
import { alpha } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import CardSectionHeader from 'src/v3/components/card-section-header'
import type { EventCertificateRequirement } from '../types'
import {
  eventCertificatePollCancelled,
  eventCertificatePollRequested,
  fetchEventProgress,
  selectEventCertificatePoll,
  selectEventCompleted,
  selectEventProgress,
} from '../events-v2.slice'
import { downloadCertificateBlob, postRestartEvent } from '../utils/api'

let requirementLabel = (req: EventCertificateRequirement): string => {
  if (req === 'certificateAttendanceNotRecorded')
    return 'Attendance not yet recorded'
  if (req === 'certificateQuizNotPassed') return 'Quiz not passed'
  return 'Survey not submitted'
}

type CertificateCardProps = {
  eventId: string
  eventName: string
  isLinkedService?: boolean
  onError: (msg: string) => void
  sessionName?: string
  sessionDisplayTime?: string
}

let CertificateCard = ({
  eventId,
  eventName,
  isLinkedService,
  onError,
  sessionName,
  sessionDisplayTime,
}: CertificateCardProps) => {
  let dispatch = useAppDispatch()
  let poll = useAppSelector(selectEventCertificatePoll(eventId))
  let progress = useAppSelector(selectEventProgress(eventId))
  let completed = useAppSelector(selectEventCompleted(eventId))
  let [downloading, setDownloading] = useState(false)
  let [restartOpen, setRestartOpen] = useState(false)
  let [restarting, setRestarting] = useState(false)

  // The card only mounts once progress is loaded (selectShowEventCertificate).
  // When it already says no certificate applies, there's nothing to poll for.
  let progressNotApplicable = progress?.certificate.kind === 'notApplicable'

  useEffect(() => {
    if (progressNotApplicable) return undefined
    dispatch(eventCertificatePollRequested(eventId))
    return () => {
      dispatch(eventCertificatePollCancelled(eventId))
    }
  }, [dispatch, eventId, progressNotApplicable])

  let certificate =
    poll?.state === 'answered' && poll.certificate.kind === 'issued'
      ? poll.certificate.certificate
      : null

  // No CE hours: nothing to download. The card only shows once the attempt is
  // completed (selectShowEventCertificate), so this confirms the credit. No
  // spinner, Restart button (the API rejects it) or generation-failure state.
  let isNotApplicable =
    progressNotApplicable ||
    (poll?.state === 'answered' && poll.certificate.kind === 'notApplicable')

  let isGenerating =
    !isNotApplicable &&
    (poll === null ||
      poll.state === 'polling' ||
      (poll.state === 'answered' && poll.certificate.kind === 'pending'))

  let hasFailed = !isNotApplicable && poll?.state === 'failed'

  let notEarnedRequirements =
    poll?.state === 'answered' && poll.certificate.kind === 'notEarned'
      ? poll.certificate.unmetRequirements
      : null

  let handleRestart = () => {
    setRestarting(true)
    postRestartEvent(eventId)
      .then(() => dispatch(fetchEventProgress(eventId)))
      .catch((err) => {
        let message = err?.response?.data
        onError(
          typeof message === 'string' && message !== ''
            ? message
            : 'Error restarting event',
        )
      })
      .finally(() => {
        setRestarting(false)
        setRestartOpen(false)
      })
  }

  let handleDownload = () => {
    if (!certificate) return
    setDownloading(true)
    downloadCertificateBlob(certificate.downloadURL)
      .then((blob) => {
        let blobUrl = window.URL.createObjectURL(blob)
        let link = document.createElement('a')
        // eslint-disable-next-line fp/no-mutation
        link.href = blobUrl
        link.setAttribute('download', `${eventName}.pdf`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        // Defer revocation — synchronous revocation races with Safari's download queue.
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100)
      })
      .catch(() => onError('Error downloading certificate'))
      .finally(() => setDownloading(false))
  }

  return (
    <>
      <Paper
        className="flex flex-col gap-3 p-6"
        sx={(theme) => ({
          border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
          borderTop: `3px solid ${theme.palette.success.main}`,
        })}
      >
        <CardSectionHeader label="Certificate" />
        {sessionName && sessionDisplayTime && (
          <Box className="flex flex-col gap-1">
            <Typography sx={{ fontWeight: 500 }}>{sessionName}</Typography>
            <Typography variant="body2" color="text.secondary">
              {sessionDisplayTime}
            </Typography>
          </Box>
        )}
        {isGenerating && (
          <Box className="flex items-center justify-center py-2">
            <CircularProgress size={20} />
          </Box>
        )}
        {certificate && (
          <Button
            variant="contained"
            color="success"
            fullWidth
            loading={downloading}
            onClick={handleDownload}
          >
            Download Certificate
          </Button>
        )}
        {isNotApplicable && (
          <Typography variant="body2" color="text.secondary">
            You have been given credit for completing this event.
          </Typography>
        )}
        {/* Mirrors the API's restart gate: FOAMfrat events only (the ones that submit to CAPCE),
            with CE hours, once the attempt is complete (its CE record has been issued). */}
        {!isLinkedService && !isNotApplicable && completed && (
          <Button
            variant="outlined"
            fullWidth
            onClick={() => setRestartOpen(true)}
          >
            Restart Event Progress
          </Button>
        )}
        {notEarnedRequirements && (
          <Box className="flex flex-col gap-1">
            <Typography variant="body2" color="text.secondary">
              Certificate requirements not yet met:
            </Typography>
            {notEarnedRequirements.map((req) => (
              <Typography key={req} variant="body2" color="text.secondary">
                · {requirementLabel(req)}
              </Typography>
            ))}
          </Box>
        )}
        {hasFailed && (
          <>
            <Typography variant="body2" color="text.secondary">
              Certificate generation is taking longer than expected.
            </Typography>
            <Button
              variant="outlined"
              color="success"
              fullWidth
              onClick={() => dispatch(eventCertificatePollRequested(eventId))}
            >
              Retry
            </Button>
          </>
        )}
      </Paper>
      <Dialog
        open={restartOpen}
        onClose={() => {
          if (!restarting) setRestartOpen(false)
        }}
        aria-labelledby="restart-dialog-title"
      >
        <Box className="flex flex-col gap-4 p-6" sx={{ minWidth: 320 }}>
          <Typography
            id="restart-dialog-title"
            variant="h6"
            className="text-center"
          >
            Confirm Event Restart
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Restarting lets you earn CE for this event again for your current
            certification cycle. You'll need to attend a new session, then
            complete the quiz and survey again. A new certificate is generated
            when you finish. Are you sure you want to restart this event?
          </Typography>
          <Box className="flex justify-end gap-2">
            <Button
              variant="outlined"
              onClick={() => setRestartOpen(false)}
              disabled={restarting}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleRestart}
              loading={restarting}
            >
              Confirm
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  )
}

export default CertificateCard
